import { GroupsOutlined, Search } from '@mui/icons-material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Chip,
    Divider,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpUser } from 'src/http/user/httpUser'
import { HttpSystems } from 'src/http/workflow/httpSystems'
import { HttpCacheGeneral } from 'src/http/httpCacheGeneral'
import { EntityWithIdFields, PaisLogoFields } from 'src/types'
import { CountryAvatar } from '../../../../../../components/icons/CountryAvatar'
import {
    UserDTOFields,
    UserGroupListResponseDTO,
    UserGroupViewDTO,
    UserGroupViewDTOFields,
    UserViewDTO
} from 'src/types/user/userData'

export type EditUserGroupsProps = {
    user: UserViewDTO
    onClose: () => void
}

type SimpleOption = { id: number; label: string }

const GRID_HEIGHT = 500
const GRID_SLOTS = { columnMenuIcon: () => <></> }

const gridSx = {
    height: '100%',
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
        bgcolor: 'background.default'
    },
    '& .MuiDataGrid-row:hover': {
        bgcolor: 'action.hover'
    },
    '& .MuiDataGrid-cell': {
        alignItems: 'center',
        py: 0.75
    }
}

const GroupCircuitCell = ({ row }: { row: UserGroupViewDTO }) => {
    const groupName = row[UserGroupViewDTOFields.GroupName]
    const countryName = row[UserGroupViewDTOFields.CountryName]
    const circuitName = row[UserGroupViewDTOFields.NombreSistema]

    return (
        <Stack spacing={0.5} sx={{ minWidth: 0, width: '100%', py: 0.5 }}>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
                <Chip
                    label={countryName}
                    size='small'
                    variant='outlined'
                    avatar={<CountryAvatar countryCode={row[UserGroupViewDTOFields.CountryCode]} />}
                    sx={{
                        maxWidth: 150,
                        flexShrink: 0,
                        '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }
                    }}
                />
                <Tooltip title={groupName || 'Sin grupo'} placement='top-start'>
                    <Typography
                        variant='body2'
                        fontWeight={600}
                        noWrap
                        sx={{ minWidth: 0, color: groupName ? 'text.primary' : 'text.disabled' }}
                    >
                        {groupName || 'Sin grupo'}
                    </Typography>
                </Tooltip>
            </Stack>
            <Tooltip title={circuitName || 'Sin circuito'} placement='top-start'>
                <Typography
                    variant='caption'
                    color={circuitName ? 'text.secondary' : 'text.disabled'}
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        lineHeight: 1.25
                    }}
                >
                    {circuitName || 'Sin circuito'}
                </Typography>
            </Tooltip>
        </Stack>
    )
}

function EditUserGroups({ user, onClose }: EditUserGroupsProps) {
    const userId = user[EntityWithIdFields.Id]

    const [filterPais, setFilterPais] = useState<SimpleOption | null>(null)
    const [filterSistema, setFilterSistema] = useState<SimpleOption | null>(null)
    const [leftSearch, setLeftSearch] = useState('')
    const [leftSelected, setLeftSelected] = useState<number[]>([])
    const [rightSelected, setRightSelected] = useState<number[]>([])
    const [isRefreshingGroups, setIsRefreshingGroups] = useState(false)
    const [locallyAvailableGroups, setLocallyAvailableGroups] = useState<UserGroupViewDTO[]>([])
    const [gruposData, setGruposData] = useState<UserGroupListResponseDTO | undefined>(undefined)

    // Local tracking of assigned group ids and their data
    const [assignedIds, setAssignedIds] = useState<number[] | undefined>(undefined)
    const rightGroupsMapRef = useRef<Map<number, UserGroupViewDTO>>(new Map())

    const hasPais = filterPais != null
    const codsPais = filterPais ? String(filterPais.id) : ''
    const codSistema = filterSistema?.id

    // Load available countries for the filter
    const { data: paisOptions = [] } = useQuery({
        queryKey: ['cache-paises'],
        queryFn: async () => {
            const data = await HttpCacheGeneral.getPaisLogo()

            return data.map(p => ({ id: p[PaisLogoFields.CodPais], label: p.descripcion }))
        },
        staleTime: Infinity
    })

    // Load sistemas for selected paises
    const { data: sistemaOptionsRaw = [] } = useQuery({
        queryKey: ['sistemas-por-pais', codsPais],
        queryFn: () => HttpSystems.getByCountries(codsPais),
        enabled: hasPais
    })

    const sistemaOptions = useMemo<SimpleOption[]>(() => {
        const options = sistemaOptionsRaw.map(s => ({ id: s.id, label: s.descripcion }))
        options.unshift({ id: -1, label: 'Sin circuito asignado' })

        return options
    }, [sistemaOptionsRaw])

    const recargarGrupos = (paisSeleccionado: SimpleOption | null, sistemaSeleccionado: SimpleOption | null) => {
        setLeftSelected([])
        setRightSelected([])
        setAssignedIds(undefined)
        setLocallyAvailableGroups([])
        setGruposData(undefined)
        rightGroupsMapRef.current = new Map()

        if (!paisSeleccionado || !sistemaSeleccionado) {
            setIsRefreshingGroups(false)

            return Promise.resolve()
        }

        const nuevosCodsPais = String(paisSeleccionado.id)
        const nuevoCodSistema = sistemaSeleccionado.id

        setIsRefreshingGroups(true)

        return HttpUser.getGroupsByUser(userId, {
            codsPais: nuevosCodsPais,
            codSistema: nuevoCodSistema
        }).then(response => {
            const asociados = response.asociados
            rightGroupsMapRef.current = new Map(
                asociados.map(g => [g[UserGroupViewDTOFields.GroupId], g])
            )
            setGruposData(response)
            setAssignedIds(asociados.map(g => g[UserGroupViewDTOFields.GroupId]))
            setIsRefreshingGroups(false)

            return response
        }).catch(error => {
            setIsRefreshingGroups(false)
            throw error
        })
    }

    const matchesSelectedCircuit = useCallback((group: UserGroupViewDTO) => {
        if (codSistema == null) return true

        const groupCodSistema = group[UserGroupViewDTOFields.CodSistema]
        if (codSistema === -1) return groupCodSistema == null || groupCodSistema <= 0

        return groupCodSistema === codSistema
    }, [codSistema])

    const assignedSet = new Set(assignedIds ?? [])

    const leftGroups = useMemo(() => {
        if (!hasPais || !gruposData || isRefreshingGroups) return []

        const gruposDisponibles = [
            ...gruposData.disponibles,
            ...locallyAvailableGroups
        ].filter((group, index, groups) =>
            groups.findIndex(g => g[UserGroupViewDTOFields.GroupId] === group[UserGroupViewDTOFields.GroupId]) === index
        )

        return gruposDisponibles
            .filter(g => !assignedSet.has(g[UserGroupViewDTOFields.GroupId]))
            .filter(
                g =>
                    !leftSearch || g[UserGroupViewDTOFields.GroupName]?.toLowerCase().includes(leftSearch.toLowerCase())
            )
    }, [gruposData, assignedIds, leftSearch, hasPais, isRefreshingGroups, locallyAvailableGroups])

    const rightGroups = useMemo(() => {
        if (!hasPais || assignedIds === undefined) return []
        const paisIds = new Set(filterPais ? [filterPais.id] : [])

        return assignedIds
            .map(id => rightGroupsMapRef.current.get(id))
            .filter((g): g is UserGroupViewDTO => g !== undefined)
            .filter(g => paisIds.has(g[UserGroupViewDTOFields.CountryCode]))
            .filter(matchesSelectedCircuit)
    }, [assignedIds, hasPais, filterPais, matchesSelectedCircuit])

    const moveRight = () => {
        if (leftSelected.length === 0) return
        const selectedGroups = leftGroups.filter(g => leftSelected.includes(g[UserGroupViewDTOFields.GroupId]))
        selectedGroups.forEach(g => rightGroupsMapRef.current.set(g[UserGroupViewDTOFields.GroupId], g))
        setAssignedIds(prev => [...new Set([...(prev ?? []), ...leftSelected])])
        setLocallyAvailableGroups(prev =>
            prev.filter(g => !leftSelected.includes(g[UserGroupViewDTOFields.GroupId]))
        )
        setLeftSelected([])
    }

    const moveLeft = () => {
        if (rightSelected.length === 0) return
        const removing = new Set(rightSelected)
        const selectedGroups = rightGroups.filter(g => removing.has(g[UserGroupViewDTOFields.GroupId]))
        setAssignedIds(prev => (prev ?? []).filter(id => !removing.has(id)))
        setLocallyAvailableGroups(prev => [
            ...prev,
            ...selectedGroups.filter(group =>
                !prev.some(g => g[UserGroupViewDTOFields.GroupId] === group[UserGroupViewDTOFields.GroupId])
            )
        ])
        setRightSelected([])
    }

    const renderLongTextCell = (value?: string | null) => (
        <Tooltip title={value || 'Sin datos'} placement='top-start'>
            <Typography
                variant='body2'
                sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    lineHeight: 1.25,
                    color: value ? 'text.primary' : 'text.disabled'
                }}
            >
                {value || 'Sin datos'}
            </Typography>
        </Tooltip>
    )

    const columns: GridColDef<UserGroupViewDTO>[] = [
        {
            headerName: 'Grupo / Circuito',
            field: UserGroupViewDTOFields.GroupName,
            flex: 1,
            minWidth: 460,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => <GroupCircuitCell row={row} />
        },
        {
            headerName: 'País',
            field: UserGroupViewDTOFields.CountryName,
            flex: 1.3,
            minWidth: 130,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ row }) => (
                <Chip
                    label={row[UserGroupViewDTOFields.CountryName]}
                    size='small'
                    variant='outlined'
                    avatar={<CountryAvatar countryCode={row[UserGroupViewDTOFields.CountryCode]} />}
                />
            )
        },
        {
            headerName: 'Circuito',
            field: UserGroupViewDTOFields.NombreSistema,
            flex: 3.5,
            minWidth: 300,
            sortable: false,
            disableColumnMenu: true,
            renderCell: ({ value }) => renderLongTextCell(value)
        }
    ]

    const onSubmit = () => {
        if (codSistema == null) {
            toast.error('Seleccione un circuito antes de guardar')

            return Promise.reject(new Error('Debe seleccionar un circuito'))
        }

        let mensajeError = 'No se pudieron guardar los grupos'
        const promise = HttpUser.updateUserGroups(userId, {
            codSistema,
            idsGrupo: assignedIds ?? []
        })
            .then(response => {
                return recargarGrupos(filterPais, filterSistema).then(() => response)
            })
            .catch(err => {
                mensajeError = err?.message || mensajeError

                throw err
            })
        toast.promise(promise, {
            success: 'Grupos actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    const userName = `${user[UserDTOFields.FirstName] ?? ''} ${user[UserDTOFields.LastName] ?? ''}`.trim()
    const userInitials = `${user[UserDTOFields.FirstName]?.[0] ?? ''}${
        user[UserDTOFields.LastName]?.[0] ?? ''
    }`.toUpperCase()
    const userMail = user[UserDTOFields.Email]

    return (
        <>
            <DialogTitle sx={{ px: 3, py: 2.5 }}>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Avatar sx={{ width: 42, height: 42, bgcolor: 'primary.main', fontSize: 15 }}>
                        {userInitials || <GroupsOutlined fontSize='small' />}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant='h6' fontWeight={700} lineHeight={1.25}>
                            Grupos de {userName || 'usuario'}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' noWrap>
                            {userMail || 'Sin email cargado'}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Chip color='primary' variant='outlined' label={`${rightGroups.length} asignados`} size='small' />
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ pt: '0 !important', px: 3, pb: 0 }}>
                <Divider sx={{ mb: 2 }} />

                <Paper
                    variant='outlined'
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 1,
                        bgcolor: 'background.default'
                    }}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Autocomplete
                            options={paisOptions}
                            value={filterPais}
                            onChange={(_, val) => {
                                setFilterPais(val)
                                setFilterSistema(null)
                                recargarGrupos(val, null)
                            }}
                            getOptionLabel={o => o.label}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            size='small'
                            sx={{ flex: 1 }}
                            renderInput={params => (
                                <TextField {...params} label='Pais *' helperText='Obligatorio para buscar grupos' />
                            )}
                        />
                        <Autocomplete
                            options={sistemaOptions}
                            value={filterSistema}
                            onChange={(_, val) => {
                                setFilterSistema(val)
                                recargarGrupos(filterPais, val)
                            }}
                            getOptionLabel={o => o.label}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            disabled={!hasPais}
                            size='small'
                            sx={{ flex: 1 }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label='Circuito'
                                    placeholder={!hasPais ? 'Seleccioná un país primero' : undefined}
                                />
                            )}
                        />
                    </Stack>
                </Paper>

                {!hasPais && (
                    <Alert severity='info' sx={{ mb: 2 }}>
                        Selecciona al menos un pais para ver los grupos disponibles y asignados del usuario.
                    </Alert>
                )}

                {/* Transfer list */}
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems='stretch'>
                    {/* Lista izquierda: disponibles */}
                    <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                            <GroupsOutlined color='action' fontSize='small' />
                            <Typography variant='subtitle2' fontWeight={700}>
                                Disponibles
                            </Typography>
                            <Chip size='small' label={leftGroups.length} variant='outlined' />
                        </Stack>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                            <TextField
                                value={leftSearch}
                                onChange={e => setLeftSearch(e.target.value)}
                                placeholder='Buscar grupo...'
                                size='small'
                                fullWidth
                                sx={{ mb: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <Search fontSize='small' />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Box sx={{ height: GRID_HEIGHT, width: '100%' }}>
                                <DataGrid
                                    rows={leftGroups}
                                    columns={columns}
                                    columnVisibilityModel={{
                                        [UserGroupViewDTOFields.CountryName]: false,
                                        [UserGroupViewDTOFields.NombreSistema]: false
                                    }}
                                    getRowId={row => row[UserGroupViewDTOFields.GroupId]}
                                    checkboxSelection
                                    rowSelectionModel={leftSelected}
                                    onRowSelectionModelChange={model => setLeftSelected(model as number[])}
                                    loading={isRefreshingGroups}
                                    density='standard'
                                    rowHeight={76}
                                    columnHeaderHeight={44}
                                    hideFooter
                                    disableColumnFilter
                                    disableColumnSelector
                                    slots={GRID_SLOTS}
                                    sx={gridSx}
                                />
                            </Box>
                        </Box>
                    </Paper>

                    {/* Botones del medio */}
                    <Stack
                        justifyContent='center'
                        alignItems='center'
                        spacing={1}
                        sx={{
                            px: { xs: 0, md: 0.5 },
                            py: { xs: 0, md: 8 },
                            flexDirection: { xs: 'row', lg: 'column' }
                        }}
                    >
                        <Tooltip title='Agregar seleccionados'>
                            <span>
                                <IconButton
                                    color='primary'
                                    disabled={leftSelected.length === 0}
                                    onClick={moveRight}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title='Quitar seleccionados'>
                            <span>
                                <IconButton
                                    color='error'
                                    disabled={rightSelected.length === 0}
                                    onClick={moveLeft}
                                    sx={{
                                        border: theme => `1px solid ${theme.palette.divider}`,
                                        bgcolor: 'background.paper'
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>

                    {/* Lista derecha: asignados */}
                    <Paper variant='outlined' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', borderRadius: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.5 }}>
                            <GroupsOutlined color='primary' fontSize='small' />
                            <Typography variant='subtitle2' fontWeight={700}>
                                Asignados
                            </Typography>
                            <Chip size='small' label={rightGroups.length} color='primary' variant='outlined' />
                        </Stack>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                            <Box sx={{ height: GRID_HEIGHT, width: '100%' }}>
                                <DataGrid
                                    rows={rightGroups}
                                    columns={columns}
                                    columnVisibilityModel={{
                                        [UserGroupViewDTOFields.CountryName]: false,
                                        [UserGroupViewDTOFields.NombreSistema]: false
                                    }}
                                    getRowId={row => row[UserGroupViewDTOFields.GroupId]}
                                    checkboxSelection
                                    rowSelectionModel={rightSelected}
                                    onRowSelectionModelChange={model => setRightSelected(model as number[])}
                                    loading={isRefreshingGroups}
                                    density='standard'
                                    rowHeight={76}
                                    columnHeaderHeight={44}
                                    hideFooter
                                    disableColumnFilter
                                    disableColumnSelector
                                    slots={GRID_SLOTS}
                                    sx={gridSx}
                                />
                            </Box>
                        </Box>
                    </Paper>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cerrar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmit}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}

export default EditUserGroups
